import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell as CTableCell,
  TableHead as CTableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Route as VoidSalesRoute } from "@/routes/(pharma)/_layout/reports/void-sales/index";
import normalizeEnums from "@/utils/normalize_enums";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  DoubleArrowLeftIcon,
  DoubleArrowRightIcon,
} from "@radix-ui/react-icons";
import { useNavigate } from "@tanstack/react-router";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { tableHeadClass, tableCellClass, tableCellLastRowClass } from "@/components/ui/table-styles";
import PaginationButton from "@/components/ui/pagination-button";
import { cn } from "@/lib/utils";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { convertToCurrencyRounded } from "@/utils/currency_converter";
dayjs.extend(customParseFormat);

const TableCell = ({ className = "", ...props }) => (
  <CTableCell className={cn(tableCellClass, "text-center", className)} {...props} />
);
const TableHead = ({ className = "", ...props }) => (
  <CTableHead className={cn(tableHeadClass, "text-center", className)} {...props} />
);

export default function Component() {
  const data = VoidSalesRoute.useLoaderData();
  const searchParams = VoidSalesRoute.useSearch();
  const navigate = useNavigate();

  // Pagination logic
  const itemsPerPage = searchParams.pageSize;
  const totalPages = data.length > 0 ? Math.ceil(data[0].full_count / itemsPerPage) : 1;
  const getPageNumbers = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      if (searchParams.page > 3) pages.push('...');
      for (let i = Math.max(2, searchParams.page - 1); i <= Math.min(searchParams.page + 1, totalPages - 1); i++) {
        pages.push(i);
      }
      if (searchParams.page < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  return (
    <div className="overflow-y-scroll h-screen pb-48">
      <div className="flex items-center justify-between">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/invoicing">
                Reports Overview
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Void Sales</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </div>
      <hr className="my-4" />
      {/* Border and radius wrapper for table and pagination */}
      <div className="border rounded-lg bg-white overflow-hidden">
        <Table className="text-sm">
          <TableHeader>
            <TableRow className="hover:bg-inherit">
              <TableHead rowSpan={2}>Created At</TableHead>
              <TableHead rowSpan={2}>Day</TableHead>
              <TableHead rowSpan={2}>Payment Type</TableHead>
              <TableHead rowSpan={2}>Patient Name</TableHead>
              <TableHead rowSpan={2}>Pharmacist</TableHead>
              <TableHead rowSpan={2}>Items</TableHead>
              <TableHead rowSpan={1} colSpan={3} className={tableHeadClass + " text-center"}>
                Amount
              </TableHead>
              <TableHead rowSpan={2}>Insurance Number</TableHead>
              <TableHead rowSpan={2}>Discount</TableHead>
              <TableHead rowSpan={2}>Vat</TableHead>
            </TableRow>
            <TableRow className="hover:bg-inherit">
              <TableHead>Cash</TableHead>
              <TableHead>Card</TableHead>
              <TableHead>Cash Received</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((item, index) => {
              const mergedItems = [...(item.medical_items || []), ...(item.non_medical_items || [])];
              return (
                <TableRow className="font-medium cursor-pointer" key={index}>
                  <TableCell className={index === data.length - 1 ? tableCellLastRowClass : ""}>
                    {dayjs(item.created_at).format("DD/MM/YYYY")}
                  </TableCell>
                  <TableCell className={index === data.length - 1 ? tableCellLastRowClass : ""}>{dayjs(item.created_at).format("dddd")}</TableCell>
                  <TableCell className={index === data.length - 1 ? tableCellLastRowClass : ""}>{normalizeEnums(item.payment_type)}</TableCell>
                  <TableCell className={index === data.length - 1 ? tableCellLastRowClass : ""}>{item.patient_name || "N/A"}</TableCell>
                  <TableCell className={index === data.length - 1 ? tableCellLastRowClass : ""}>{item.user_name || "N/A"}</TableCell>
                  <TableCell className={index === data.length - 1 ? tableCellLastRowClass : ""}>
                    {mergedItems.length === 0 ? (
                      "N/A"
                    ) : mergedItems.length <= 2 ? (
                      mergedItems.join(", ")
                    ) : (
                      <Popover>
                        <PopoverTrigger asChild>
                          <button className="underline text-blue-600 cursor-pointer bg-transparent border-none p-0 m-0">
                            {mergedItems.slice(0, 2).join(", ")} +{mergedItems.length - 2} more
                          </button>
                        </PopoverTrigger>
                        <PopoverContent>
                          <ul className="list-disc pl-4">
                            {mergedItems.map((item, idx) => (
                              <li key={idx}>{item}</li>
                            ))}
                          </ul>
                        </PopoverContent>
                      </Popover>
                    )}
                  </TableCell>
                  <TableCell className={index === data.length - 1 ? tableCellLastRowClass : ""}>{`${convertToCurrencyRounded(item.amount_values.cash)}`}</TableCell>
                  <TableCell className={index === data.length - 1 ? tableCellLastRowClass : ""}>{`${convertToCurrencyRounded(item.amount_values.card)}`}</TableCell>
                  <TableCell className={index === data.length - 1 ? tableCellLastRowClass : ""}>{`${convertToCurrencyRounded(item.amount_values.cash_received)}`}</TableCell>
                  <TableCell className={index === data.length - 1 ? tableCellLastRowClass : ""}>
                    {item.insurance_details.erx_number || "N/A"}
                  </TableCell>
                  <TableCell className={index === data.length - 1 ? tableCellLastRowClass : ""}>{`${convertToCurrencyRounded(item.amount_values.total_discount)}`}</TableCell>
                  <TableCell className={index === data.length - 1 ? tableCellLastRowClass : ""}>{`${convertToCurrencyRounded(item.amount_values.vat)}`}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {/* Pagination and count controls below table */}
        <div className="flex items-center justify-between py-4 px-6 text-sm border-t bg-white">
          <p>{data.length > 0 ? data[0].full_count : 0} <span className="text-gray-500 font-light">count</span></p>
          <div className="flex items-center gap-1">
            <PaginationButton
              onClick={() => {
                navigate({
                  search: {
                    ...searchParams,
                    page: 1
                  }
                });
              }}
              disabled={searchParams.page === 1}
            >
              <DoubleArrowLeftIcon className="h-4 w-4" />
            </PaginationButton>
            <PaginationButton
              onClick={() => {
                navigate({
                  search: {
                    ...searchParams,
                    page: searchParams.page - 1
                  }
                });
              }}
              disabled={searchParams.page === 1}
            >
              <ChevronLeftIcon className="h-4 w-4" />
            </PaginationButton>
            {getPageNumbers().map((page, idx) => (
              page === '...'
                ? <span key={`ellipsis-${idx}`} className="mx-1">...</span>
                : <PaginationButton
                    key={page}
                    active={searchParams.page === page}
                    onClick={() => {
                      navigate({
                        search: {
                          ...searchParams,
                          page: typeof page === 'number' ? page : searchParams.page
                        }
                      });
                    }}
                  >
                    <span className="text-sm">{page}</span>
                  </PaginationButton>
            ))}
            <PaginationButton
              onClick={() => {
                navigate({
                  search: {
                    ...searchParams,
                    page: searchParams.page + 1
                  }
                });
              }}
              disabled={searchParams.page === totalPages}
            >
              <ChevronRightIcon className="h-4 w-4" />
            </PaginationButton>
            <PaginationButton
              onClick={() => {
                navigate({
                  search: {
                    ...searchParams,
                    page: totalPages
                  }
                });
              }}
              disabled={searchParams.page === totalPages}
            >
              <DoubleArrowRightIcon className="h-4 w-4" />
            </PaginationButton>
          </div>
        </div>
      </div>
    </div>
  );
}
